import { useEffect, useRef, useState } from 'react';
import { Button } from '@moneyskyline/ui';
import {
  createReceiptOperationId,
  ReceiptRequestError,
  useReceipts,
  type ReceiptBlobHandle,
  type ReceiptOut,
} from '@/api/hooks/useReceipts';

const MAX_RECEIPT_BYTES = 10 * 1024 * 1024;
const ACCEPTED_TYPES = new Set(['image/jpeg', 'image/png', 'application/pdf']);
const ACCEPT_ATTRIBUTE = 'image/jpeg,image/png,application/pdf';

type ReceiptSlotProps = {
  lineItemId?: string;
  lineItemVersion?: number;
  canonicalWriteContext?: string;
  transactionId?: string;
  receipts?: ReceiptOut[];
  onReload?: () => void;
};

type FailedUpload = { file: File; operationId: string };
type FailedDelete = { receiptId: string; operationId: string };
type Preview = { receiptId: string; handle: Extract<ReceiptBlobHandle, { kind: 'image' }> };

function receiptLabel(receipt: ReceiptOut, index: number) {
  return `${receipt.content_type === 'application/pdf' ? 'PDF' : 'Image'} receipt ${index + 1}`;
}

export function ReceiptSlot({
  lineItemId,
  lineItemVersion,
  canonicalWriteContext,
  transactionId,
  receipts = [],
  onReload,
}: ReceiptSlotProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const confirmButtonRef = useRef<HTMLButtonElement>(null);
  const viewButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [validationError, setValidationError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [pendingUpload, setPendingUpload] = useState(false);
  const [failedUpload, setFailedUpload] = useState<FailedUpload | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [viewError, setViewError] = useState<string | null>(null);
  const [confirmingReceiptId, setConfirmingReceiptId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<FailedDelete | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [failedDelete, setFailedDelete] = useState<FailedDelete | null>(null);

  const receiptsApi = useReceipts({
    lineItemId: lineItemId ?? '',
    lineItemVersion: lineItemVersion ?? 1,
    canonicalWriteContext: canonicalWriteContext ?? '',
    transactionId: transactionId ?? '',
  });
  const canUpload = Boolean(lineItemId && lineItemVersion && canonicalWriteContext && transactionId);

  useEffect(() => () => preview?.handle.revoke(), [preview]);
  useEffect(() => {
    if (confirmingReceiptId) confirmButtonRef.current?.focus();
  }, [confirmingReceiptId]);

  function submitUpload(file: File, operationId: string) {
    setValidationError(null);
    setUploadError(null);
    setUploadSuccess(false);
    setFailedUpload(null);
    setPendingUpload(true);
    setUploadProgress(0);
    receiptsApi.upload.mutate(
      { file, operationId, onProgress: setUploadProgress },
      {
        onSuccess: () => {
          setPendingUpload(false);
          setUploadProgress(100);
          setUploadSuccess(true);
          setFailedUpload(null);
          onReload?.();
          inputRef.current?.focus();
        },
        onError: (error) => {
          setPendingUpload(false);
          const requestError = error instanceof ReceiptRequestError ? error : null;
          if (requestError?.code === 'receipt_storage_unavailable') {
            setUploadError('Receipt storage is unavailable.');
            setFailedUpload(null);
          } else if (requestError?.status === 409) {
            setUploadError('This receipt request could not be completed. Refresh to check.');
            setFailedUpload(null);
            onReload?.();
          } else if (requestError?.status === undefined) {
            setUploadError('We couldn’t confirm whether the receipt uploaded. Refresh to check before trying again.');
            setFailedUpload({ file, operationId });
          } else {
            setUploadError('Receipt could not be uploaded. Try again.');
            setFailedUpload({ file, operationId });
          }
          inputRef.current?.focus();
        },
      },
    );
  }

  function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!ACCEPTED_TYPES.has(file.type) || file.size > MAX_RECEIPT_BYTES) {
      setValidationError('Choose a JPEG, PNG, or PDF file up to 10 MiB.');
      setUploadError(null);
      return;
    }
    let operationId: string;
    try {
      operationId = createReceiptOperationId();
    } catch {
      setUploadError('Receipt could not be uploaded. Try again.');
      return;
    }
    submitUpload(file, operationId);
  }

  async function handleView(receipt: ReceiptOut) {
    setViewError(null);
    try {
      const handle = await receiptsApi.viewOrDownload(receipt);
      if (handle.kind !== 'image') return;
      setPreview({ receiptId: receipt.receipt_id, handle });
    } catch (error) {
      const requestError = error instanceof ReceiptRequestError ? error : null;
      if (requestError?.code === 'receipt_storage_unavailable') {
        setViewError('Receipt storage is unavailable.');
      } else if (requestError?.status === 409) {
        setViewError('This receipt could not be opened. Refresh to check.');
      } else {
        setViewError('Receipt could not be opened. Try again.');
      }
    }
  }

  function closePreview() {
    if (!preview) return;
    const button = viewButtonRefs.current[preview.receiptId];
    preview.handle.revoke();
    setPreview(null);
    button?.focus();
  }

  function confirmDelete(receiptId: string) {
    setConfirmingReceiptId(receiptId);
    setDeleteError(null);
    setFailedDelete(null);
  }

  function submitDelete({ receiptId, operationId }: FailedDelete) {
    setDeleteError(null);
    setFailedDelete(null);
    setPendingDelete({ receiptId, operationId });
    const selected = receipts.find((item) => item.receipt_id === receiptId);
    receiptsApi.deleteReceipt.mutate(
      { receiptId, operationId, expectedVersion: selected?.version ?? 1 },
      {
        onSuccess: () => {
          setPendingDelete(null);
          setConfirmingReceiptId(null);
          onReload?.();
        },
        onError: (error) => {
          setPendingDelete(null);
          const requestError = error instanceof ReceiptRequestError ? error : null;
          if (requestError?.status === 409) {
            setDeleteError('This receipt could not be removed. Refresh to check.');
            setFailedDelete(null);
            onReload?.();
          } else {
            setDeleteError('Receipt could not be removed. Try again.');
            setFailedDelete({ receiptId, operationId });
          }
        },
      },
    );
  }

  function handleDeleteConfirm() {
    if (!confirmingReceiptId) return;
    let operationId: string;
    try {
      operationId = createReceiptOperationId();
    } catch {
      setDeleteError('Receipt could not be removed. Try again.');
      return;
    }
    submitDelete({ receiptId: confirmingReceiptId, operationId });
  }

  return (
    <section data-testid="receipt-slot" className="mt-3 rounded-md border border-border p-3 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-medium">Receipts</h3>
          <p className="text-xs text-muted-foreground">JPEG, PNG, or PDF up to 10 MiB.</p>
        </div>
        {canUpload ? (
          <label className={`ms-tap-44 inline-flex min-h-11 cursor-pointer items-center rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground focus-within:ring-2 focus-within:ring-ring ${pendingUpload ? 'pointer-events-none cursor-not-allowed opacity-50' : ''}`}>
            Add receipt
            <input
              ref={inputRef}
              type="file"
              className="sr-only"
              accept={ACCEPT_ATTRIBUTE}
              onChange={handleFileChange}
              disabled={pendingUpload}
              aria-label="Add receipt"
            />
          </label>
        ) : (
          <span className="text-xs text-muted-foreground">Receipt upload is unavailable for this item.</span>
        )}
      </div>

      {validationError && <p role="alert" className="mt-2 text-sm text-degraded-foreground">{validationError}</p>}
      {uploadError && (
        <div role="alert" className="mt-2 rounded-md border border-degraded-border bg-degraded-surface p-2 text-sm">
          <p>{uploadError}</p>
          {failedUpload && (
            <Button type="button" size="sm" variant="outline" className="ms-tap-44 mt-2 min-h-11" onClick={() => {
              try {
                const nextId = uploadError?.includes('couldn’t confirm')
                  ? failedUpload.operationId
                  : createReceiptOperationId();
                submitUpload(failedUpload.file, nextId);
              } catch {
                setUploadError('Receipt could not be uploaded. Try again.');
              }
            }}>
              Try upload again
            </Button>
          )}
        </div>
      )}
      {pendingUpload && uploadProgress !== null && (
        <div className="mt-2" role="status" aria-live="polite">
          <div className="mb-1 flex justify-between text-xs text-muted-foreground">
            <span>Uploading receipt…</span><span>{uploadProgress}%</span>
          </div>
          <div role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={uploadProgress} className="h-2 overflow-hidden rounded-full bg-secondary">
            <div className="h-full bg-primary transition-all" style={{ width: `${uploadProgress}%` }} />
          </div>
        </div>
      )}
      {uploadSuccess && <p role="status" className="mt-2 text-sm text-muted-foreground">Receipt uploaded. Refreshing details…</p>}
      {viewError && <p role="alert" className="mt-2 text-sm text-degraded-foreground">{viewError}</p>}

      {receipts.length > 0 ? (
        <ul className="mt-3 space-y-2" aria-label="Receipts list">
          {receipts.map((receipt, index) => (
            <li key={receipt.receipt_id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-2">
              <span>{receiptLabel(receipt, index)}</span>
              <div className="flex flex-wrap gap-2">
                <Button
                  ref={(button) => { viewButtonRefs.current[receipt.receipt_id] = button; }}
                  type="button"
                  size="sm"
                  variant="outline"
                  className="ms-tap-44 min-h-11"
                  onClick={() => void handleView(receipt)}
                  aria-label={`${receipt.content_type === 'application/pdf' ? 'Download' : 'View'} receipt ${index + 1}`}
                >
                  {receipt.content_type === 'application/pdf' ? 'Download' : 'View'}
                </Button>
                <Button type="button" size="sm" variant="ghost" className="ms-tap-44 min-h-11" onClick={() => confirmDelete(receipt.receipt_id)} aria-label={`Remove receipt ${index + 1}`}>
                  Remove
                </Button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-3 text-xs text-muted-foreground">No receipts attached.</p>
      )}

      {confirmingReceiptId && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Confirm receipt removal"
          className="mt-3 rounded-md border border-degraded-border bg-degraded-surface p-2 text-sm"
          onKeyDown={(event) => {
            if (event.key === 'Escape') setConfirmingReceiptId(null);
          }}
        >
          {pendingDelete ? (
            <p role="status">Removing receipt…</p>
          ) : deleteError ? (
            <div role="alert">
              <p>{deleteError}</p>
              {failedDelete && <Button type="button" size="sm" variant="outline" className="ms-tap-44 mt-2 min-h-11" onClick={() => submitDelete(failedDelete)}>Try remove again</Button>}
            </div>
          ) : (
            <>
              <p>
                Remove {receiptLabel(receipts.find((item) => item.receipt_id === confirmingReceiptId) ?? receipts[0], Math.max(0, receipts.findIndex((item) => item.receipt_id === confirmingReceiptId)))} from this transaction? It will be removed from the app while storage cleanup is pending.
              </p>
              <div className="mt-2 flex gap-2">
                <Button ref={confirmButtonRef} type="button" size="sm" variant="destructive" className="ms-tap-44 min-h-11" onClick={handleDeleteConfirm}>Confirm remove receipt</Button>
                <Button type="button" size="sm" variant="outline" className="ms-tap-44 min-h-11" onClick={() => setConfirmingReceiptId(null)}>Cancel</Button>
              </div>
            </>
          )}
        </div>
      )}

      {preview && (
        <div className="mt-3 rounded-md border border-border p-2" role="dialog" aria-label="Receipt image preview">
          <div className="flex justify-end">
            <Button type="button" size="sm" variant="ghost" onClick={closePreview} aria-label="Close receipt preview">Close</Button>
          </div>
          <img src={preview.handle.url} alt="Receipt image" className="max-h-96 max-w-full object-contain" />
        </div>
      )}
    </section>
  );
}

export default ReceiptSlot;
